import React, { useState, useRef, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useRecipe } from '../hooks/useRecipe';
import RecipeResultScreen from './RecipeResultScreen';
import { deviceDetector } from '../services/deviceDetector';
import carbonaraImage from '../assets/carbonara.png';
 
// Добавляем глобальные стили для шрифта Roboto
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');
  
  * {
    font-family: 'Roboto', -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
`;

const MockupContainer = styled(motion.div)`
  position: relative;
  width: 320px;
  height: 640px;
  margin: 0 auto;
  z-index: 1;
  left: 0;
  padding: 0;
 `;

const PhoneFrame = styled(motion.div)`
  position: absolute;
  width: 100%;
  top: -50px;
  height: 100%;
  border-radius: 45px;
  background: #0A0A0A;
  box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.5),
              0 10px 20px -5px rgba(0, 0, 0, 0.4),
              inset 0 0 0 1px rgba(100, 100, 100, 0.2),
              -4px 0 5px -3px rgba(0, 0, 0, 0.7),
              4px 0 5px -3px rgba(0, 0, 0, 0.7);
  overflow: hidden;
  border: 10px solid #222;
  display: flex;
  flex-direction: column;
   transform: scale(0.94);
 
`;

const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  background: #0F0F0F;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border-radius: 35px;
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 50%;
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  margin: 0;
  padding: 0;
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 305px;
  left: 0;
  right: 0;
  background: linear-gradient(
    to bottom,
    transparent 20%,
    rgba(15, 15, 15, 0.7) 70%,
    #0F0F0F 100%
  );
  z-index: 10000;
  margin: 0;
  padding: 0;
`;

const ContentContainer = styled.div`
  position: absolute;
  bottom: 50px;
  left: 0;
  right: 0;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 25px;
  background: transparent;
`;

const Title = styled.div`
  color: white;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const SettingsIcon = styled.div`
  width: 20px;
  height: 20px;
  opacity: 0.8;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InputContainer = styled.div`
  background: rgba(30, 30, 30, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 16px;
  min-height: 100px;
  position: relative;
  box-shadow: 0 0 15px rgba(108, 47, 242, 0.7), inset 0 0 8px rgba(138, 43, 226, 0.4);
  border: 1px solid rgba(138, 43, 226, 0.6);
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  outline: none;
  resize: none;
  min-height: 80px;
  overflow-y: auto;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const CharCounter = styled.div`
  position: absolute;
  bottom: 8px;
  right: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
`;

const CreateButton = styled.button`
  background: linear-gradient(to right, #6C2FF2, #8A2BE2);
  border: none;
  border-radius: 25px;
  padding: 16px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const DynamicIsland = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 34px;
  background: #000000;
  border-radius: 20px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05);
  
  &::after {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    background: #333;
    border-radius: 50%;
    right: 15px;
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 30px;
    height: 4px;
    background: #333;
    border-radius: 2px;
    left: 15px;
  }
`;

const SideButton = styled.div`
  position: absolute;
  background: #0A0A0A;
  border-radius: 2px;
  z-index: 11;
  
  &.power {
    width: 4px;
    height: 60px;
    right: 5px;
      
    top: 140px;
    border-left: 1px solid #777;
  }
  
  &.volume-up {
    width: 4px;
    height: 40px;
    left: 6px;
    top: 130px;
    border-right: 1px solid #777;
  }
  
  &.volume-down {
    width: 4px;
    height: 40px;
    left: 6px;
    top: 185px;
    border-right: 1px solid #777;
  }
  
  &.silent-switch {
    width: 5px;
    height: 18px;
    left: 6px;
    top: 80px;
    border-right: 1px solid #777;
    border-radius: 2px;
  }
`;

const FrameHighlight = styled.div`
  position: absolute;
  z-index: 10;
  
  &.top {
    top: -2px;
    left: 0px;
    right: 70px;
    height: 1px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
  }
  
  &.bottom {
    bottom: -2px;
    left: 70px;
    right: 70px;
    height: 1px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 50%;
  }
`;

const StatusBar = styled.div`
  height: 44px;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  // backdrop-filter: blur(10px);
`;

const TimeDisplay = styled.div`
  text-align: left;
`;

const StatusIcons = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const BatteryIcon = styled.div`
  width: 25px;
  height: 12px;
  border: 1.5px solid white;
  border-radius: 3px;
  padding: 1px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 18px;
    background-color: white;
    margin: 1px;
  }
  
  &::after {
    content: '';
    position: absolute;
    right: -4px;
    top: 3px;
    width: 2px;
    height: 4px;
    border-radius: 0 2px 2px 0;
    background-color: white;
  }
`;

const SignalIcon = styled.div`
  display: flex;
  align-items: flex-end;
  height: 12px;
  gap: 1px;
  
  span {
    width: 3px;
    background-color: white;
    border-radius: 1px 1px 0 0;
    
    &:nth-child(1) { height: 5px; }
    &:nth-child(2) { height: 7px; }
    &:nth-child(3) { height: 9px; }
    &:nth-child(4) { height: 11px; }
  }
`;

const ErrorText = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ResetButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 25px;
  padding: 12px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ScreenContainer = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #0F0F0F;
  border-radius: 40px;
  overflow: hidden;
`;

const ResultContainer = styled.div`
  background: rgba(60, 60, 60, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 16px;
  min-height: 150px;
  position: relative;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
`;

const SettingsModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  width: 320px;
  background: #120F0D;
  border-radius: 12px;
  padding: 20px;
  position: relative;
`;

const ModalTitle = styled.div`
  font-family: 'Roboto';
  font-weight: 700;
  font-size: 18px;
  color: #FDF6EC;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;
`;

const ToolItem = styled(motion.div)`
  background: rgba(58, 51, 46, 0.6);
  border-radius: 12px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const ToolIcon = styled.img`
  width: 40px;
  height: 40px;
  opacity: 0.7;
`;

const ToolName = styled.div`
  color: white;
  font-size: 12px;
  text-align: center;
`;

const ModalText = styled.div`
  color: #E6DAC8;
  font-size: 14px;
  line-height: 1.4;
  margin: 20px 0;
  text-align: center;
`;

const NonIOSOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 100;
  border-radius: 40px;
`;

const OverlayText = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.4;
`;

const AppStoreButton = styled(motion.a)`
  background: linear-gradient(to right, #6C2FF2, #8A2BE2);
  border: none;
  border-radius: 25px;
  padding: 16px 32px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const AppStoreLogo = styled.img`
  width: 24px;
  height: 24px;
`;

const AppContent = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const BackgroundWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, #6832DC 0%, #341A78 10%, black 100%, #000000 100%);
  z-index: -1;
`;

const PhoneMockup = ({ videoSrc, videoSrc2 }) => {
  const videoRef = useRef(null);
  const [inputValue, setInputValue] = useState('Pasta Carbonara');
  const {
    isLoading,
    error,
    recipe,
    step,
    processUserInput,
    reset,
    setMockRecipe,
  } = useRecipe();
  console.log('🚀 recipe', recipe);
  const [showSettings, setShowSettings] = useState(false);
  const isIOSOwner = deviceDetector.isIOSOwner();
  // const isIOSOwner = true;
  
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.error('Ошибка воспроизведения видео:', e));
    }
  }, []);

  const handleSubmit = async () => {
    if (inputValue === 'Pasta Carbonara') {
      const mockRecipe = [{
        title: "Pasta Carbonara",
        description: "Classic Italian pasta dish with crispy bacon, eggs and Parmesan cheese",
        usedIngredients: ["🍝 Spaghetti", "🥓 Bacon", "🥚 Eggs", "🧀 Parmesan", "🌶️ Black Pepper"],
        level: "Medium",
        cooktime: "20 min",
        calories: "650 kcal",
        steps: [
          "Cook spaghetti in salted water until al dente",
          "Fry chopped bacon until crispy",
          "Mix eggs, grated Parmesan and black pepper in a bowl",
          "Drain pasta, add bacon and egg mixture, quickly stir together",
          "Serve immediately with extra cheese and freshly ground pepper"
        ]
      }];
      mockRecipe.imageUrl = carbonaraImage;
      
      setMockRecipe(mockRecipe);
    } else {
      await processUserInput(inputValue);
    }
  };

  const handleBack = () => {
    reset();
  };

  const tools = [
    { name: 'Oven', icon: '🔥' },
    { name: 'Pan', icon: '🍳' },
    { name: 'Pot', icon: '🥘' },
    { name: 'Blender', icon: '🥤' },
    { name: 'Microwave', icon: '📱' },
    { name: 'Cat', icon: '🐱' }
  ];

  return (
    <>
      <GlobalStyle />
      <MockupContainer
        animate={{ 
          y: [0, -10, 0],
          rotateZ: [0, 0.5, 0]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      >
        <SideButton className="power" />
        <SideButton className="volume-up" />
        <SideButton className="volume-down" />
        <SideButton className="silent-switch" />
        
        <PhoneFrame>
          <FrameHighlight className="top" />
          <FrameHighlight className="bottom" />
          <DynamicIsland />
          
          <PhoneScreen>
            <StatusBar>
              <TimeDisplay>9:41</TimeDisplay>
              <StatusIcons>
                <SignalIcon>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </SignalIcon>
                <BatteryIcon />
              </StatusIcons>
            </StatusBar>
            
            <AppContent>
              {!isIOSOwner ? (
                <>
                  <VideoContainer style={{ height: '100%' }}>
                    <VideoElement 
                      ref={videoRef}
                      src={videoSrc2} 
                      loop 
                      muted 
                      playsInline
                      style={{ height: '100%', width: '100%' }}
                    />
                  </VideoContainer>
                </>
              ) : (
                <AnimatePresence mode="wait">
                  {step === 'result' && recipe ? (
                    <ScreenContainer
                      key="result-screen"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ 
                        duration: 0.5,
                        ease: "easeInOut"
                      }}
                    >
                      <RecipeResultScreen 
                        recipe={recipe} 
                        isLoading={isLoading}
                        onBack={handleBack}
                      />
                    </ScreenContainer>
                  ) : (
                    <ScreenContainer
                      key="input-screen"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ 
                        duration: 0.5,
                        ease: "easeInOut"
                      }}
                    >
                      <StatusBar>
                        <TimeDisplay>9:41</TimeDisplay>
                        <StatusIcons>
                          <SignalIcon>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                          </SignalIcon>
                          <BatteryIcon />
                        </StatusIcons>
                      </StatusBar>
                      
                      <VideoContainer>
                        <VideoElement 
                          ref={videoRef}
                          src={videoSrc} 
                          loop 
                          muted 
                          playsInline
                        />
                      </VideoContainer>
                      <GradientOverlay />

                      <ContentContainer
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ delay: 0.2 }}
                      >
                        <Title>
                          What's in the dish?
                        </Title>
                        
                        {step === 'input' && (
                          <>
                            <InputContainer>
                              <StyledTextArea
                                placeholder="Carbonara, protein pudding, etc."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                maxLength={50}
                                style={{ 
                                  fontWeight: '600', 
                                  color: 'white', 
                                  fontSize: '16px', 
                                  fontFamily: 'Black han sans', 
                                  textShadow: '0 0 10px black',
                                  }}
                              />
                              <CharCounter>{inputValue.length}/50</CharCounter>
                            </InputContainer>
                            
                            <CreateButton 
                              onClick={handleSubmit}
                            >
                              Try demo
                            </CreateButton>
                          </>
                        )}
                        
                        {step === 'loading' && (
                          <>
                            <InputContainer>
                              <StyledTextArea
                                value={inputValue}
                                disabled
                                maxLength={50}
                              />
                              <CharCounter>{inputValue.length}/50</CharCounter>
                            </InputContainer>
                            
                            <CreateButton disabled>
                              <LoadingSpinner />
                              Processing...
                            </CreateButton>
                          </>
                        )}
                        
                        {step === 'error' && (
                          <ResultContainer>
                            <ErrorText>{error}</ErrorText>
                            <ResetButton onClick={reset}>
                              Try again
                            </ResetButton>
                          </ResultContainer>
                        )}
                      </ContentContainer>
                    </ScreenContainer>
                  )}
                </AnimatePresence>
              )}
            </AppContent>
            
            <DynamicIsland />
          </PhoneScreen>
        </PhoneFrame>
      </MockupContainer>

      <AnimatePresence>
        {showSettings && (
          <SettingsModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ModalTitle>
                <span>🔧</span> Settings
              </ModalTitle>
              
              <ToolsGrid>
                {tools.map((tool, index) => (
                  <ToolItem
                    key={tool.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <span style={{ fontSize: '24px' }}>{tool.icon}</span>
                    <ToolName>{tool.name}</ToolName>
                  </ToolItem>
                ))}
              </ToolsGrid>

              <ModalText>
                For detailed personalization and access to all features, please download our mobile app
              </ModalText>

              <AppStoreButton
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.6 }}
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  onClick={(e) => {
    e.preventDefault(); // чтобы не переходило мгновенно
    if (typeof window !== 'undefined' && window.gtag_report_conversion) {
      window.gtag_report_conversion('https://apps.apple.com/us/app/chefkats-ai-snap-cook/id6739225693');
    } else {
      // fallback — если функция не сработала
      window.location.href = 'https://apps.apple.com/us/app/chefkats-ai-snap-cook/id6739225693';
    }
  }}
>
  on App Store
</AppStoreButton>
            </ModalContent>
          </SettingsModal>
        )}
      </AnimatePresence>
    </>
  );
};

export default PhoneMockup; 